import React from 'react';
import { Trash2, ShoppingCart } from 'lucide-react';
import { NgapAct } from '../../types/ngap';
import ShareButton from '../ShareButton';
import { generateNgapCodeLabel } from '../../utils/urlUtils';

interface ActHeaderProps {
  act: NgapAct;
  isInCart?: boolean;
  onToggleCartItem?: (act: NgapAct) => void;
  darkMode: boolean;
  showRemoveButton?: boolean;
  displayCotationType?: 'current' | 'july2025' | 'july2026' | 'sept2026' | 'july2027';
}

const ActHeader: React.FC<ActHeaderProps> = ({
  act,
  isInCart = false,
  onToggleCartItem,
  darkMode,
  showRemoveButton = false,
  displayCotationType
}) => {
  const ngapCodeLabel = React.useMemo(() =>
    generateNgapCodeLabel(act, displayCotationType),
    [act, displayCotationType]
  );

  const handleClick = React.useCallback(() => {
    if (onToggleCartItem) {
      onToggleCartItem(act);
    }
  }, [onToggleCartItem, act]);

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onToggleCartItem) {
        onToggleCartItem(act);
      }
    }
  }, [onToggleCartItem, act]);

  const isSelected = isInCart;

  return (
    <div className="mb-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${
          darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'
        }`}>
          {act.reeducation || 'Rééducation'}
        </p>
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-md text-lg font-bold font-mono ${
            darkMode
              ? 'bg-appines-emerald/15 text-appines-emerald'
              : 'bg-appines-glass/25 text-appines-petrol'
          }`}
          aria-label={`Code cotation ${ngapCodeLabel}`}
        >
          {ngapCodeLabel}
        </span>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0" role="group" aria-label="Actions pour cet acte">
        <ShareButton act={act} darkMode={darkMode} />
        {onToggleCartItem && (
          <button
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={`p-2 rounded-lg transition-colors ${
              isSelected
                ? showRemoveButton
                  ? 'text-appines-petal hover:bg-appines-petal/10'
                  : 'text-appines-emerald hover:bg-appines-emerald/10'
                : darkMode
                  ? 'text-appines-white/45 hover:text-appines-emerald hover:bg-appines-white/10'
                  : 'text-appines-petrol/45 hover:text-appines-emerald hover:bg-[rgba(1,42,54,0.06)]'
            }`}
            aria-label={
              showRemoveButton
                ? `Retirer ${act.libelle} du panier`
                : `Ajouter ${act.libelle} au panier`
            }
          >
            {showRemoveButton ? (
              <Trash2 className="h-5 w-5" />
            ) : (
              <ShoppingCart
                className={`h-5 w-5 ${isSelected ? 'fill-current' : ''}`}
              />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default ActHeader;
